/**
 * Indicator Management
 * Design indicators in the Studio, capture data no system holds, and read the
 * published values in the scorecard and explorer. All of it runs on the
 * indicator registry (/api/v1/indicators/).
 */
import React, { useState } from 'react';
import {
  Alert, Box, Typography, Tabs, Tab, Chip, Breadcrumbs, CircularProgress, Link,
} from '@mui/material';
import {
  GridOn, TableChart, Transform as MappingIcon, Flag as FlagIcon,
  FactCheck as FactCheckIcon, Insights as StudioIcon, UploadFile as CaptureIcon, Description as ProfileIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import IndicatorStudio from '../../components/Indicators/Studio/IndicatorStudio';
import IndicatorScorecard from '../../components/Indicators/Studio/IndicatorScorecard';
import IndicatorExplorer from '../../components/Indicators/Studio/IndicatorExplorer';
import DataCapture from '../../components/Indicators/Studio/DataCapture';
import DistrictProfile from '../../components/Indicators/Studio/DistrictProfile';
import ZestpKpiDashboard from '../ZestpKpi/ZestpKpiDashboard';
import UISTrackerDashboard from '../UISTracker/UISTrackerDashboard';
import DataMapping from '../SISIntegration/components/DataMapping';
import { Catalogue, errorMessage, indicatorRegistryApi } from '../../services/indicatorRegistryApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>
);

/** Renders its child once the measure catalogue has loaded. */
const WithCatalogue: React.FC<{ children: (catalogue: Catalogue) => React.ReactNode }> = ({ children }) => {
  const { data, isLoading, error } = useQuery(['indicator-registry', 'catalogue'], indicatorRegistryApi.catalogue);
  if (isLoading) return <CircularProgress />;
  if (!data) return <Alert severity="error">{errorMessage(error)}</Alert>;
  return <>{children(data)}</>;
};

const sourceChip = (label: string, bg: string, color: string) => (
  <Chip label={label} size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: bg, color }} />
);

const IndicatorDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 1 }}>
          <Link underline="hover" color="inherit" href="/">Dashboard</Link>
          <Typography color="text.primary">Indicators</Typography>
        </Breadcrumbs>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Indicator Management
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Design indicators from SIS, TTPDS and captured data, approve them, and publish their values
        </Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
        }}
      >
        <Tab icon={<StudioIcon />} label="Indicator Studio" iconPosition="start" />
        <Tab icon={<GridOn />} label="Scorecard" iconPosition="start" />
        <Tab icon={<TableChart />} label="Explore" iconPosition="start" />
        <Tab icon={<ProfileIcon />} label="District Profile" iconPosition="start" />
        <Tab icon={<CaptureIcon />} label="Data Capture" iconPosition="start" />
        <Tab
          icon={<MappingIcon />}
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Data Mapping {sourceChip('SIS', '#E8F5E9', '#2E7D32')}</Box>}
          iconPosition="start"
        />
        <Tab
          icon={<FlagIcon />}
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>ZESTP KPIs {sourceChip('GPE', '#FFF3E0', '#E65100')}</Box>}
          iconPosition="start"
        />
        <Tab
          icon={<FactCheckIcon />}
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>UIS Tracker {sourceChip('GPE', '#FFF3E0', '#E65100')}</Box>}
          iconPosition="start"
        />
      </Tabs>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <IndicatorStudio />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <IndicatorScorecard />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <WithCatalogue>{catalogue => <IndicatorExplorer catalogue={catalogue} />}</WithCatalogue>
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <DistrictProfile />
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <WithCatalogue>{catalogue => <DataCapture catalogue={catalogue} />}</WithCatalogue>
      </TabPanel>

      <TabPanel value={activeTab} index={5}>
        <DataMapping />
      </TabPanel>

      <TabPanel value={activeTab} index={6}>
        <ZestpKpiDashboard />
      </TabPanel>

      <TabPanel value={activeTab} index={7}>
        <UISTrackerDashboard />
      </TabPanel>
    </Box>
  );
};

export default IndicatorDashboard;
